import React, { useCallback, useContext } from 'react';
import clsx from 'clsx';
import { AppBar, Toolbar, IconButton, Box, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { UpliftBetterHelpStackedWhiteIcon } from '@uplift/icons';
import { MainLayoutType, ProfileMenuItem } from '@uplift/types';
import { MainLayoutContext } from './main-layout.provider';
import { UpliftDrawer } from './uplift-drawer.component';
import { ScrollableView } from '../components/ScrollableView.component';
import { ProfileMenu } from '../components/ProfileMenu';
import { Loader } from '../../Loader';
import { useMessagingContext } from '../../Messaging';
import { useLegacyLayoutClasses } from '../legacyLayoutClasses';


export type MainComponentProps = {
  className?: string;
  noContainerPadding?: boolean;
};


export type MainLayoutProps = {
  menuItems?: Array<ProfileMenuItem>;
  className?: string;
  showAvatar?: boolean;
  bodyScroll?: boolean;
  loading?: boolean;
  initialIsDrawerOpened?: boolean;
  MainComponent?: React.ComponentType<MainComponentProps>;
  appbarClassName?: string;
  overrideMenuIconClick?: () => void;
};


export const DefaultMainComponent: React.FC<MainComponentProps> = ({ className, ...props }) => {
  return (
    <ScrollableView>
      <main
        {...props}
        className={className}
        style={{
          flexGrow: 1,
        }}
      />
    </ScrollableView>
  );
};


export const MainLayout: React.FC<MainLayoutType & MainLayoutProps> = ({
  children,
  className,
  useLayoutConf,
  menuItems,
  showAvatar = true,
  loading = false,
  initialIsDrawerOpened = true,
  MainComponent = DefaultMainComponent,
  appbarClassName,
  overrideMenuIconClick
}) => {
  const messaging = useMessagingContext();
  const { classes } = useLegacyLayoutClasses({});
  const { isDrawerOpened, setIsDrawerOpened } = useContext(MainLayoutContext);
  const contentClassName = clsx(classes.content, className);

  const onMenuIconClick = useCallback(() => {
    if (overrideMenuIconClick) {
      overrideMenuIconClick();
    } else {
      setIsDrawerOpened(prev => !prev);
    }
  }, [ overrideMenuIconClick, setIsDrawerOpened ]);

  const body = loading ? <Loader color="primary" height="37em" /> : children;

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={clsx(classes.appBar, appbarClassName)}>
        <Toolbar className={classes.toolBar}>
          <IconButton
            color="inherit"
            onClick={onMenuIconClick}
            edge="start"
            className={classes.menuButton}
            data-testid="sidebarToggleHamburgerButton"
          >
            {isDrawerOpened ? (
              <ChevronLeftIcon />
            ) : (
              <Badge badgeContent={messaging.unreadMessagesCount} color="error">
                <MenuIcon />
              </Badge>
            )}
          </IconButton>
          <UpliftBetterHelpStackedWhiteIcon />
          <Box display="flex" flexGrow={1} textAlign="right" justifyContent="flex-end">
            <ProfileMenu items={menuItems} showAvatar={showAvatar} />
          </Box>
        </Toolbar>
      </AppBar>

      <Box display="flex" width="100%">
        <UpliftDrawer useLayoutConf={useLayoutConf} initialIsDrawerOpened={initialIsDrawerOpened} />
        <Box display="flex" flexDirection="column" width="100%" className={contentClassName}>
          <Toolbar />
          <MainComponent>
            <div className={classes.body}>
              <React.Suspense fallback={<Loader color="primary" height="37em" />}>
                {body}
              </React.Suspense>
            </div>
          </MainComponent>
        </Box>
      </Box>
    </div>
  );
};
